import { HubConnection } from '@microsoft/signalr'
import { useEffect, useRef } from 'react'
import { CertificateDocumentModel } from '../../../shared/models/CertificateDocument.model'

const UPLOAD_SUCCESS_EVENT = 'ecm-uploading-certificate-success'
const UPLOAD_FAILED_EVENT = 'ecm-uploading-certificate-failed'

const isSameDocumentId = (left?: string, right?: string) =>
  !!left && !!right && left.toLowerCase() === right.toLowerCase()

/**
 * The per document `documentUpdated` broadcast is published to the `CertificateDocument_{id}` group.
 * The upload completion notice for the same operation is published to the signed in user's own
 * group, which every screen already receives. Listening to it lets a screen refresh the affected
 * documents from the API even when the per document broadcast never arrives.
 */
export const useEcmUploadCompletionRefresh = (
  connection: HubConnection | undefined,
  certificateDocumentIds: string[],
  fetchDocumentById: (documentId: string) => Promise<CertificateDocumentModel | null>,
  onDocumentUpdate: (document: CertificateDocumentModel) => void
) => {
  // Refs keep the long lived hub handler pointed at the latest values rather than a render snapshot.
  const documentIdsRef = useRef<string[]>(certificateDocumentIds)
  documentIdsRef.current = certificateDocumentIds

  const fetchDocumentByIdRef = useRef(fetchDocumentById)
  fetchDocumentByIdRef.current = fetchDocumentById

  const onDocumentUpdateRef = useRef(onDocumentUpdate)
  onDocumentUpdateRef.current = onDocumentUpdate

  useEffect(() => {
    if (!connection) return

    const handleUploadCompleted = async (result: unknown) => {
      let payload: any

      try {
        payload = typeof result === 'string' ? JSON.parse(result) : result
      } catch {
        return
      }

      const notifiedIds: string[] = payload?.certificateDocumentIds ?? []
      if (notifiedIds.length === 0) return

      const displayedIds = documentIdsRef.current || []
      const idsToRefresh = notifiedIds.filter(notifiedId =>
        displayedIds.some(displayedId => isSameDocumentId(displayedId, notifiedId))
      )

      if (idsToRefresh.length === 0) return

      const documents = await Promise.all(
        idsToRefresh.map(id => fetchDocumentByIdRef.current(id).catch(() => null))
      )

      documents.forEach(document => {
        if (document) onDocumentUpdateRef.current(document)
      })
    }

    connection.on(UPLOAD_SUCCESS_EVENT, handleUploadCompleted)
    connection.on(UPLOAD_FAILED_EVENT, handleUploadCompleted)

    return () => {
      connection.off(UPLOAD_SUCCESS_EVENT, handleUploadCompleted)
      connection.off(UPLOAD_FAILED_EVENT, handleUploadCompleted)
    }
  }, [connection])
}
